<template>
    <fieldset v-if="design === 'as-sentence'" class="flex flex-wrap items-center gap-3">
        <legend class="sr-only">{{ label }}</legend>
        <div
            v-for="(item, index) in items"
            :key="item.contentId"
        >
            <span v-if="index === items.length - 1" class="mr-2">And</span>
            <button
                type="button"
                class="rounded-full border border-dark-blue px-4 py-2 text-sm transition-colors"
                :class="selectedSlugs.includes(item.slug)
                    ? 'bg-dark-blue text-background'
                    : 'bg-background text-text hover:bg-dark-blue/60 hover:text-background'"
                :aria-pressed="selectedSlugs.includes(item.slug)"
                @click="toggleSentenceItem(item.slug)"
            >
                {{ localizedName(item.name) }}
            </button>
        </div>
    </fieldset>

    <div v-else ref="dropdownRoot" class="relative">
        <button
            ref="trigger"
            type="button"
            class="flex items-center gap-2 rounded-full border border-dark-blue bg-background px-4 py-2 text-sm text-text transition-colors hover:bg-dark-blue/60 hover:text-background"
            :aria-expanded="isOpen"
            :aria-controls="listId"
            aria-haspopup="listbox"
            @click="toggleDropdown"
            @keydown.down.prevent="openAndFocus(0)"
            @keydown.up.prevent="openAndFocus(-1)"
            @keydown.esc.stop.prevent="closeDropdown()"
        >
            <span>{{ selectedItem ? localizedName(selectedItem.name) : label }}</span>
            <svg
                aria-hidden="true"
                viewBox="0 0 20 20"
                class="h-4 w-4 fill-current transition-transform"
                :class="{ 'rotate-180': isOpen }"
            >
                <path d="m5.3 7.5 4.7 4.7 4.7-4.7 1.1 1.1-5.8 5.8-5.8-5.8 1.1-1.1Z"/>
            </svg>
        </button>

        <ul
            v-if="isOpen"
            :id="listId"
            ref="list"
            role="listbox"
            :aria-label="label"
            class="absolute right-0 top-full z-30 mt-2 min-w-full overflow-hidden rounded-2xl border border-dark-blue bg-background py-1 shadow-xl"
            @keydown="onListKeydown"
        >
            <li>
                <button
                    type="button"
                    role="option"
                    class="flex w-full items-center whitespace-nowrap px-4 py-2 text-left text-sm text-text transition-colors hover:bg-dark-blue/10 focus-visible:bg-dark-blue/10 focus-visible:outline-none"
                    :aria-selected="!selectedSlug"
                    @click="selectItem(null)"
                >
                    <span class="mr-2 flex w-5 shrink-0 justify-center" aria-hidden="true">
                        <span
                            v-if="!selectedSlug"
                            data-selected-indicator
                            class="fill-dark-blue"
                        >
                            <checkmark-icon class="w-3 h-3" />
                        </span>
                    </span>
                    <span class="flex-1">{{ showAllLabel }}</span>
                    <span
                        v-if="showAllCount !== undefined"
                        class="ml-6 tabular-nums text-text/60"
                    >
                        {{ showAllCount }}
                    </span>
                </button>
            </li>
            <li v-for="item in items" :key="item.contentId">
                <button
                    type="button"
                    role="option"
                    class="flex w-full items-center whitespace-nowrap px-4 py-2 text-left text-sm text-text transition-colors hover:bg-dark-blue/10 focus-visible:bg-dark-blue/10 focus-visible:outline-none"
                    :aria-selected="selectedSlug === item.slug"
                    @click="selectItem(item.slug)"
                >
                    <span class="mr-2 flex w-5 shrink-0 justify-center" aria-hidden="true">
                        <span
                            v-if="selectedSlug === item.slug"
                            data-selected-indicator
                            class="fill-dark-blue"
                        >
                            <checkmark-icon class="w-3 h-3" />
                        </span>
                    </span>
                    <span class="flex-1">{{ localizedName(item.name) }}</span>
                    <span
                        v-if="optionCounts[item.slug] !== undefined"
                        class="ml-6 tabular-nums text-text/60"
                    >
                        {{ optionCounts[item.slug] }}
                    </span>
                </button>
            </li>
        </ul>
    </div>
</template>

<script setup lang="ts">
import { onClickOutside } from '@vueuse/core'
import type { CaseTaxonomy } from '#shared/types/WorkPage'
import type { LocalizedText } from '#layers/cms/shared/types/bucketData'
import CheckmarkIcon from "#layers/cms/app/components/icons/checkmarkIcon.vue"

const props = withDefaults(defineProps<{
    design: 'as-sentence' | 'dropdown'
    label: string
    items: CaseTaxonomy[]
    selectedSlugs?: string[]
    singleSelect?: boolean
    selectedSlug?: string | null
    showAllLabel?: string
    optionCounts?: Record<string, number>
    showAllCount?: number
    locale: string
}>(), {
    selectedSlugs: () => [],
    singleSelect: false,
    selectedSlug: null,
    showAllLabel: 'Show All',
    optionCounts: () => ({}),
})

const emit = defineEmits<{
    change: [slugs: string[]]
    select: [slug: string | null]
}>()

const dropdownRoot = ref<HTMLElement | null>(null)
const trigger = ref<HTMLButtonElement | null>(null)
const list = ref<HTMLElement | null>(null)
const isOpen = ref(false)
const instanceId = getCurrentInstance()?.uid ?? 0
const listId = `filter-group-list-${instanceId}`

const selectedItem = computed(() => (
    props.items.find(item => item.slug === props.selectedSlug) ?? null
))

const localizedName = (name: LocalizedText): string => {
    if (typeof name[props.locale] === 'string') return name[props.locale]
    if (typeof name.en_GB === 'string') return name.en_GB
    return Object.values(name).find(value => typeof value === 'string') ?? ''
}

const toggleSentenceItem = (slug: string) => {
    const isSelected = props.selectedSlugs.includes(slug)
    const nextSelection = props.singleSelect
        ? (isSelected ? [] : [slug])
        : (isSelected
            ? props.selectedSlugs.filter(selectedSlug => selectedSlug !== slug)
            : [...props.selectedSlugs, slug])

    emit('change', nextSelection)
}

const optionElements = () => (
    Array.from(list.value?.querySelectorAll<HTMLButtonElement>('[role="option"]') ?? [])
)

const focusOption = async (index: number) => {
    await nextTick()
    const options = optionElements()
    const normalizedIndex = index < 0 ? options.length - 1 : index
    options[normalizedIndex]?.focus()
}

const openAndFocus = (index: number) => {
    isOpen.value = true
    void focusOption(index)
}

const closeDropdown = (restoreFocus = false) => {
    isOpen.value = false
    if (restoreFocus) void nextTick(() => trigger.value?.focus())
}

const toggleDropdown = () => {
    isOpen.value = !isOpen.value
}

const selectItem = (slug: string | null) => {
    emit('select', slug)
    closeDropdown(true)
}

const onListKeydown = (event: KeyboardEvent) => {
    const options = optionElements()
    const currentIndex = options.indexOf(document.activeElement as HTMLButtonElement)

    if (event.key === 'ArrowDown') {
        event.preventDefault()
        options[(currentIndex + 1) % options.length]?.focus()
    } else if (event.key === 'ArrowUp') {
        event.preventDefault()
        options[(currentIndex - 1 + options.length) % options.length]?.focus()
    } else if (event.key === 'Home') {
        event.preventDefault()
        options[0]?.focus()
    } else if (event.key === 'End') {
        event.preventDefault()
        options.at(-1)?.focus()
    } else if (event.key === 'Escape') {
        event.preventDefault()
        closeDropdown(true)
    } else if (event.key === 'Tab') {
        closeDropdown()
    }
}

onClickOutside(dropdownRoot, () => closeDropdown())
</script>
